import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Upload,
  message,
  Space,
  Tag,
  Descriptions,
  Statistic,
  Row,
  Col,
  Divider,
} from 'antd';
import { PlusOutlined, FileOutlined, EyeOutlined, DollarOutlined } from '@ant-design/icons';
import api, { getImageUrl } from '../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

const { TextArea } = Input;

const MiCajaChica = () => {
  const { user } = useAuth();
  const [cajaChica, setCajaChica] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [comprobanteFile, setComprobanteFile] = useState([]);

  useEffect(() => {
    if (user && user.personal_id) {
      fetchCajaChica();
      fetchGastos();
    }
  }, [user]);

  const fetchCajaChica = async () => {
    if (!user || !user.personal_id) return;
    
    try {
      const response = await api.get(`/caja-chica?personal_id=${user.personal_id}`);
      if (response.data && Object.keys(response.data).length > 0) {
        setCajaChica(response.data);
      } else {
        setCajaChica(null);
      }
    } catch (error) {
      console.error('Error al cargar caja chica:', error);
    }
  };

  const fetchGastos = async () => {
    if (!user || !user.personal_id) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/gastos-caja-chica?personal_id=${user.personal_id}`);
      setGastos(response.data || []);
    } catch (error) {
      message.error('Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!user || !user.personal_id) {
      message.warning('No se encontró información del personal');
      return;
    }

    try {
      // Subir comprobante si existe
      let comprobanteUrl = null;
      if (comprobanteFile.length > 0 && comprobanteFile[0].originFileObj) {
        const formData = new FormData();
        formData.append('imagenes[]', comprobanteFile[0].originFileObj);
        formData.append('tipo', 'gastos');

        try {
          const uploadResponse = await api.post('/gastos-caja-chica/upload-comprobante', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          if (uploadResponse.data.urls && uploadResponse.data.urls.length > 0) {
            comprobanteUrl = uploadResponse.data.urls[0];
          }
        } catch (error) {
          message.error('Error al subir comprobante');
          return;
        }
      }

      const data = {
        personal_id: user.personal_id,
        tipo_gasto: values.tipo_gasto,
        monto: values.monto,
        descripcion: values.descripcion,
        fecha_gasto: values.fecha_gasto ? values.fecha_gasto.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        comprobante: comprobanteUrl,
        observaciones: values.observaciones,
      };

      await api.post('/gastos-caja-chica', data);
      message.success('Gasto registrado exitosamente');
      setModalVisible(false);
      form.resetFields();
      setComprobanteFile([]);
      fetchGastos();
      fetchCajaChica();
    } catch (error) {
      message.error(error.response?.data?.message || 'Error al registrar gasto');
    }
  };

  const getTipoGastoColor = (tipo) => {
    const colores = {
      operativo: 'blue',
      combustible: 'orange',
      peaje: 'green',
      otro: 'default',
    };
    return colores[tipo] || 'default';
  };

  const getEstadoColor = (estado) => {
    const colores = {
      pendiente: 'orange',
      aprobado: 'green',
      rechazado: 'red',
    };
    return colores[estado] || 'default';
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'fecha_gasto',
      key: 'fecha_gasto',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Tipo de Gasto',
      dataIndex: 'tipo_gasto',
      key: 'tipo_gasto',
      render: (tipo) => (
        <Tag color={getTipoGastoColor(tipo)}>
          {tipo ? tipo.toUpperCase() : '-'}
        </Tag>
      ),
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      render: (monto) => monto ? `S/ ${parseFloat(monto).toFixed(2)}` : '-',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      render: (text) => text || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => (
        <Tag color={getEstadoColor(estado)}>
          {estado ? estado.toUpperCase() : 'PENDIENTE'}
        </Tag>
      ),
    },
    {
      title: 'Comprobante',
      key: 'comprobante',
      render: (_, record) => {
        if (!record.comprobante) return '-';
        
        // Construir URL del comprobante
        let comprobanteUrl = record.comprobante;
        
        // Si ya es una URL absoluta, usarla directamente
        if (!comprobanteUrl.startsWith('http://') && !comprobanteUrl.startsWith('https://')) {
          // Si es una ruta relativa, construir la URL completa
          const baseURL = process.env.REACT_APP_API_URL || 'http://localhost/backend';
          const base = baseURL.replace('/backend', '');
          
          if (comprobanteUrl.startsWith('/backend/')) {
            comprobanteUrl = base + comprobanteUrl;
          } else if (comprobanteUrl.startsWith('/')) {
            comprobanteUrl = base + '/backend' + comprobanteUrl;
          } else {
            // Si no tiene prefijo, asumir que está en /backend/uploads/gastos/
            comprobanteUrl = base + '/backend/uploads/gastos/' + comprobanteUrl;
          }
        }
        
        return (
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => window.open(comprobanteUrl, '_blank')}
          >
            Ver
          </Button>
        );
      },
    },
  ];

  if (!user || !user.personal_id) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <p>No se encontró información del personal. Por favor, contacta al administrador.</p>
        </Card>
      </div>
    );
  }

  const isMobile = window.innerWidth < 768;

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: isMobile ? '18px' : '24px' }}>Mi Caja Chica</h2>
        </div>

        {cajaChica ? (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={24} md={8}>
                <Card>
                  <Statistic
                    title="Monto Asignado"
                    value={parseFloat(cajaChica.monto_asignado || 0).toFixed(2)}
                    prefix={<DollarOutlined />}
                    suffix="S/"
                    valueStyle={{ color: '#1890ff', fontSize: isMobile ? '16px' : '24px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Card>
                  <Statistic
                    title="Monto Disponible"
                    value={parseFloat(cajaChica.monto_disponible || 0).toFixed(2)}
                    prefix={<DollarOutlined />}
                    suffix="S/"
                    valueStyle={{ color: '#3f8600', fontSize: isMobile ? '16px' : '24px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Card>
                  <Statistic
                    title="Monto Utilizado"
                    value={(parseFloat(cajaChica.monto_asignado || 0) - parseFloat(cajaChica.monto_disponible || 0)).toFixed(2)}
                    prefix={<DollarOutlined />}
                    suffix="S/"
                    valueStyle={{ color: '#cf1322', fontSize: isMobile ? '16px' : '24px' }}
                  />
                </Card>
              </Col>
            </Row>

            <Card
              title="Registrar Nuevo Gasto"
              extra={
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    form.resetFields();
                    form.setFieldsValue({
                      fecha_gasto: dayjs(),
                    });
                    setComprobanteFile([]);
                    setModalVisible(true);
                  }}
                >
                  Nuevo Gasto
                </Button>
              }
            >
              <Table
                columns={columns}
                dataSource={gastos}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
                size={isMobile ? 'small' : 'middle'}
              />
            </Card>
          </>
        ) : (
          <Card>
            <p>No tienes caja chica asignada. Contacta al administrador para que te asigne un monto.</p>
          </Card>
        )}

        <Modal
          title="Registrar Nuevo Gasto"
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
            setComprobanteFile([]);
          }}
          footer={null}
          width={isMobile ? '95%' : 600}
          style={{ top: isMobile ? 10 : 20 }}
          bodyStyle={{ maxHeight: isMobile ? '80vh' : 'auto', overflowY: 'auto' }}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="tipo_gasto"
              label="Tipo de Gasto"
              rules={[{ required: true, message: 'Selecciona el tipo de gasto' }]}
            >
              <Select placeholder="Selecciona el tipo de gasto">
                <Select.Option value="operativo">Operativo</Select.Option>
                <Select.Option value="combustible">Combustible</Select.Option>
                <Select.Option value="peaje">Peaje</Select.Option>
                <Select.Option value="otro">Otro</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="monto"
              label="Monto"
              rules={[{ required: true, message: 'Ingresa el monto' }]}
            >
              <InputNumber
                min={0}
                precision={2}
                style={{ width: '100%' }}
                placeholder="0.00"
              />
            </Form.Item>

            <Form.Item
              name="fecha_gasto"
              label="Fecha del Gasto"
              rules={[{ required: true, message: 'Selecciona la fecha' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item
              name="descripcion"
              label="Descripción"
              rules={[{ required: true, message: 'Ingresa la descripción' }]}
            >
              <TextArea rows={3} placeholder="Describe el gasto realizado" />
            </Form.Item>

            <Form.Item label="Comprobante">
              <Upload
                name="comprobante"
                listType="picture-card"
                fileList={comprobanteFile}
                onChange={({ fileList }) => {
                  setComprobanteFile(fileList);
                }}
                beforeUpload={() => false}
                accept="image/*,.pdf"
                maxCount={1}
                onPreview={(file) => {
                  window.open(file.url || file.thumbUrl, '_blank');
                }}
                customRequest={({ onSuccess }) => {
                  setTimeout(() => {
                    onSuccess("ok");
                  }, 0);
                }}
              >
                {comprobanteFile.length < 1 && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Subir</div>
                  </div>
                )}
              </Upload>
              <p style={{ fontSize: '12px', color: '#999', marginTop: 8 }}>
                Sube una imagen o PDF del comprobante (máx. 10MB)
              </p>
            </Form.Item>

            <Form.Item name="observaciones" label="Observaciones">
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  Registrar Gasto
                </Button>
                <Button
                  onClick={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setComprobanteFile([]);
                  }}
                >
                  Cancelar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default MiCajaChica;

